import * as actionTypes from '../actions/types';
import {
  AnteProyectoValidation,
  BackofficeStats,
  BackofficeFilters,
  BackofficeTab,
  CuratedJobRequestItem,
  CuratedJobRequestSummary,
  CuratedPostulation,
} from '../../types/backoffice.ts';
import {
  AdminAnalyticsFilters,
  AnalyticsOverviewResponse,
} from '../../types/adminAnalytics.ts';
import { getInitialAdminAnalyticsFilters } from '../actions/adminAnalyticsActions.ts';

export interface BackofficeState {
  activeTab: BackofficeTab;
  
  pendingAnteProyectos: AnteProyectoValidation[];
  selectedAnteProyecto: AnteProyectoValidation | null;
  anteProyectosLoading: boolean;
  anteProyectosError: string | null;
  
  stats: BackofficeStats;
  statsLoading: boolean;
  
  pagination: {
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
    totalPages: number;
  };
  
  filters: BackofficeFilters;
  
  validationLoading: boolean;

  curatedPublisherUserId: number | null;
  curatedJobRequests: CuratedJobRequestItem[];
  curatedJobRequestsLoading: boolean;
  curatedJobRequestsError: string | null;
  curatedSelectedJobRequest: CuratedJobRequestSummary | null;
  curatedPostulations: CuratedPostulation[];
  curatedPostulationsLoading: boolean;
  curatedPostulationsError: string | null;

  analyticsOverview: AnalyticsOverviewResponse | null;
  analyticsLoading: boolean;
  analyticsError: string | null;
  analyticsFilters: AdminAnalyticsFilters;
}

const INITIAL_STATE: BackofficeState = {
  activeTab: 'dashboard',
  
  pendingAnteProyectos: [],
  selectedAnteProyecto: null,
  anteProyectosLoading: false,
  anteProyectosError: null,
  
  stats: {
    totalPendingIdentities: 0,
    totalPendingAnteProyectos: 0,
    totalApprovedToday: 0,
    totalRejectedToday: 0,
  },
  statsLoading: false,
  
  pagination: {
    currentPage: 1,
    itemsPerPage: 9,
    totalItems: 0,
    totalPages: 0,
  },
  
  filters: {
    searchTerm: '',
    status: 'all',
    dateFrom: null,
    dateTo: null,
    minBudget: null,
    maxBudget: null,
    categoryId: null,
  },
  
  validationLoading: false,
  curatedPublisherUserId: null,
  curatedJobRequests: [],
  curatedJobRequestsLoading: false,
  curatedJobRequestsError: null,
  curatedSelectedJobRequest: null,
  curatedPostulations: [],
  curatedPostulationsLoading: false,
  curatedPostulationsError: null,

  analyticsOverview: null,
  analyticsLoading: false,
  analyticsError: null,
  analyticsFilters: getInitialAdminAnalyticsFilters(),
};

const backofficeReducer = (state = INITIAL_STATE, action: any): BackofficeState => {
  switch (action.type) {
    case actionTypes.SET_BACKOFFICE_ACTIVE_TAB:
      return {
        ...state,
        activeTab: action.payload,
      };
      
    case actionTypes.FETCH_PENDING_ANTEPROYECTOS_LOADING:
      return {
        ...state,
        anteProyectosLoading: true,
        anteProyectosError: null,
      };
      
    case actionTypes.FETCH_PENDING_ANTEPROYECTOS_SUCCESS:
      return {
        ...state,
        pendingAnteProyectos: action.payload.data,
        pagination: {
          ...state.pagination,
          currentPage: action.payload.page,
          totalItems: action.payload.total,
          totalPages: action.payload.totalPages,
        },
        anteProyectosLoading: false,
        anteProyectosError: null,
      };
      
    case actionTypes.FETCH_PENDING_ANTEPROYECTOS_ERROR:
      return {
        ...state,
        anteProyectosLoading: false,
        anteProyectosError: action.payload,
      };
      
    case actionTypes.SET_SELECTED_ANTEPROYECTO:
      return {
        ...state,
        selectedAnteProyecto: action.payload,
      };
      
    case actionTypes.FETCH_BACKOFFICE_STATS_LOADING:
      return {
        ...state,
        statsLoading: true,
      };
      
    case actionTypes.FETCH_BACKOFFICE_STATS_SUCCESS:
      return {
        ...state,
        stats: action.payload,
        statsLoading: false,
      };
      
    case actionTypes.SET_BACKOFFICE_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
        },
        pagination: {
          ...state.pagination,
          currentPage: 1,
        },
      };
      
    case actionTypes.SET_BACKOFFICE_PAGE:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          currentPage: action.payload,
        },
      };
      
    case actionTypes.VALIDATE_ANTEPROYECTO_LOADING:
      return {
        ...state,
        validationLoading: true,
      };
      
    case actionTypes.VALIDATE_ANTEPROYECTO_SUCCESS:
      return {
        ...state,
        validationLoading: false,
        pendingAnteProyectos: state.pendingAnteProyectos.filter(
          (proyecto) => proyecto.id.toString() !== action.payload.id.toString()
        ),
        selectedAnteProyecto: null,
      };
      
    case actionTypes.VALIDATE_ANTEPROYECTO_ERROR:
      return {
        ...state,
        validationLoading: false,
      };

    case actionTypes.FETCH_CURATED_JOB_REQUESTS_LOADING:
      return {
        ...state,
        curatedJobRequestsLoading: true,
        curatedJobRequestsError: null,
      };

    case actionTypes.FETCH_CURATED_JOB_REQUESTS_SUCCESS:
      return {
        ...state,
        curatedPublisherUserId: action.payload.publisherUserId ?? null,
        curatedJobRequests: action.payload.jobRequests ?? [],
        curatedJobRequestsLoading: false,
        curatedJobRequestsError: null,
      };

    case actionTypes.FETCH_CURATED_JOB_REQUESTS_ERROR:
      return {
        ...state,
        curatedJobRequestsLoading: false,
        curatedJobRequestsError: action.payload,
      };

    case actionTypes.FETCH_CURATED_POSTULATIONS_LOADING:
      return {
        ...state,
        curatedPostulationsLoading: true,
        curatedPostulationsError: null,
      };

    case actionTypes.FETCH_CURATED_POSTULATIONS_SUCCESS:
      return {
        ...state,
        curatedSelectedJobRequest: action.payload.jobRequest ?? null,
        curatedPostulations: action.payload.postulations ?? [],
        curatedPostulationsLoading: false,
        curatedPostulationsError: null,
      };

    case actionTypes.FETCH_CURATED_POSTULATIONS_ERROR:
      return {
        ...state,
        curatedPostulationsLoading: false,
        curatedPostulationsError: action.payload,
      };

    case actionTypes.SET_ADMIN_ANALYTICS_FILTERS:
      return {
        ...state,
        analyticsFilters: {
          ...state.analyticsFilters,
          ...action.payload,
        },
      };

    case actionTypes.FETCH_ADMIN_ANALYTICS_LOADING:
      return {
        ...state,
        analyticsLoading: true,
        analyticsError: null,
      };

    case actionTypes.FETCH_ADMIN_ANALYTICS_SUCCESS:
      return {
        ...state,
        analyticsOverview: action.payload,
        analyticsLoading: false,
        analyticsError: null,
      };

    case actionTypes.FETCH_ADMIN_ANALYTICS_ERROR:
      return {
        ...state,
        analyticsLoading: false,
        analyticsError: action.payload,
      };
      
    default:
      return state;
  }
};

export default backofficeReducer;

